import { Alert, Button, Card, CardContent, Chip, CircularProgress, Divider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { N8nDomainsSettingsView } from '../../../api/src/n8n-domains/n8n-domains.types';
import type { DailyScheduleSettings } from '../../../api/src/common/daily-schedule.types';
import { DailyScheduleFields } from '../components/DailyScheduleFields';
import { Stack, Typography } from '../components/mui-system';
import { fakeDate, fakeError } from '../components/fakes-presentation';

const fields = (s: DailyScheduleSettings): DailyScheduleSettings => ({
  revision: s.revision,
  enabled: s.enabled,
  collectTime: s.collectTime,
  timeZone: s.timeZone,
});
const statuses: Record<string, string> = {
  pending: 'В очереди',
  processing: 'Обновляется',
  completed: 'Обновлено',
  failed: 'Ошибка',
  cancelled: 'Отменено',
};
export function N8nDomainsPanel() {
  const [saved, setSaved] = useState<N8nDomainsSettingsView | null>(null),
    [draft, setDraft] = useState<DailyScheduleSettings | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const live = useRef(true),
    locked = useRef(false),
    generation = useRef(0);
  useEffect(() => {
    live.current = true;
    let pending = false;
    const load = async () => {
      if (pending || locked.current) return;
      pending = true;
      const request = ++generation.current;
      try {
        const s = await api.n8nDomainsSettings();
        if (live.current && request === generation.current) {
          setSaved(s);
          setDraft((d) => d ?? fields(s));
        }
      } catch (e) {
        if (live.current) setError(fakeError(e));
      } finally {
        pending = false;
      }
    };
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 5000);
    return () => {
      live.current = false;
      clearInterval(timer);
    };
  }, []);
  async function act(save: boolean) {
    if (locked.current || !draft) return;
    locked.current = true;
    generation.current++;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const s = save ? await api.saveN8nDomainsSettings(draft) : await api.syncN8nDomains();
      if (live.current) {
        setSaved(s);
        if (save) setDraft(fields(s));
        setNotice(save ? 'Расписание сохранено.' : s.status.error ? '' : 'Обновление завершено.');
      }
    } catch (e) {
      if (live.current) setError(fakeError(e));
    } finally {
      locked.current = false;
      if (live.current) setBusy(false);
    }
  }
  const dirty = Boolean(saved && draft && JSON.stringify(fields(saved)) !== JSON.stringify(draft));
  return (
    <Card sx={{ maxWidth: 950 }}>
      <CardContent>
        <Stack gap={2}>
          <Typography variant="h3">Наши домены · n8n</Typography>
          <Typography color="text.secondary">
            Один реестр для всех workspace. Обновляется независимо от XMLStock и Telegram-отчётов.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          {!saved || !draft ? (
            !error && <CircularProgress aria-label="Загрузка настроек n8n" />
          ) : (
            <>
              {!saved.status.configured && (
                <Alert severity="warning">
                  Подключение n8n не настроено. URL, имя заголовка и секрет задаются в окружении
                  сервера.
                </Alert>
              )}
              {saved.status.error && (
                <Alert severity="error">
                  {saved.status.error}. Последняя успешная версия сохранена.
                </Alert>
              )}
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={`${saved.status.count} доменов`} />
                <Chip
                  color={saved.status.stale ? 'warning' : 'success'}
                  label={saved.status.stale ? 'Реестр устарел или недоступен' : 'Реестр актуален'}
                />
                {saved.syncing && <Chip label="Идёт обновление" />}
              </Stack>
              <Typography variant="body2">
                Успешное обновление: {fakeDate(saved.status.syncedAt)} · Последняя попытка:{' '}
                {fakeDate(saved.status.attemptedAt)}
              </Typography>
              <DailyScheduleFields
                value={draft}
                onChange={setDraft}
                disabled={busy}
                label="Обновлять n8n-реестр ежедневно"
                timeLabel="Время обновления n8n"
              />
              <Typography variant="body2">
                Следующий автозапуск:{' '}
                {saved.nextRunAt
                  ? `${new Date(saved.nextRunAt).toLocaleString('ru-RU', { timeZone: saved.timeZone })} (${saved.timeZone})`
                  : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Если время сегодня уже прошло, свободный дневной запуск будет поставлен в очередь в
                ближайшую минуту. Ручное обновление не занимает дневной слот.
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  disabled={busy || !dirty}
                  onClick={() => void act(true)}
                >
                  Сохранить расписание n8n
                </Button>
                <Button
                  disabled={busy || saved.syncing || !saved.status.configured}
                  onClick={() => void act(false)}
                >
                  Обновить сейчас
                </Button>
                <Button disabled={busy || !dirty} onClick={() => setDraft(fields(saved))}>
                  Отменить изменения
                </Button>
              </Stack>
              <Divider />
              <Typography variant="h4">Последние автозапуски</Typography>
              {!saved.recent.length && (
                <Typography color="text.secondary">Автозапусков ещё не было.</Typography>
              )}
              {saved.recent.map((row) => (
                <Stack key={row.id} gap={0.5}>
                  <Typography>
                    {row.day} · {statuses[row.status] ?? row.status} · попыток: {row.attempts}
                  </Typography>
                  {row.error && (
                    <Typography color="error.main" variant="body2">
                      {row.error}
                    </Typography>
                  )}
                </Stack>
              ))}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
